/**
 * Contrato de resposta da API do TurboPro (mesma família da ItWorks.Api):
 * { totalItens?, data, status: { resultado, mensagem } }, com erro de negócio
 * vindo em HTTP 200 + status.resultado === false. Payloads em PascalCase; os
 * tipos de cada endpoint devem refletir isso, nunca renomear campo.
 *
 * Ponto único de leitura do envelope — evita cada Api.ts tratar o formato à
 * própria maneira.
 */

export interface ApiStatus {
  resultado: boolean;
  mensagem: string;
}

export interface ApiResponse<T> {
  totalItens?: number;
  data: T;
  status: ApiStatus;
}

/** Erro de NEGÓCIO: a API respondeu, mas disse "não" (HTTP 200 + resultado false). */
export class BusinessError extends Error {
  readonly kind = 'business';
  constructor(mensagem: string) {
    super(mensagem || 'A operação não pôde ser concluída.');
    this.name = 'BusinessError';
  }
}

/** Erro de TRANSPORTE/HTTP: a API não respondeu 2xx. */
export class HttpError extends Error {
  readonly kind = 'http';
  constructor(
    readonly status: number,
    mensagem?: string
  ) {
    super(mensagem || `Erro HTTP ${status}`);
    this.name = 'HttpError';
  }
}

export interface Unwrapped<T> {
  data: T;
  totalItens: number;
}

/**
 * Converte o envelope em dado ou lança BusinessError.
 * `resultado === false` é erro MESMO com HTTP 200 — é assim que a API sinaliza
 * falha de negócio, e o front não pode tratar como sucesso silencioso.
 */
export function unwrap<T>(resposta: ApiResponse<T>): Unwrapped<T> {
  if (resposta?.status?.resultado === false) {
    throw new BusinessError(resposta.status.mensagem);
  }
  return { data: resposta.data, totalItens: resposta.totalItens ?? 0 };
}

/**
 * O dialeto de LISTA VAZIA do backend: vários `Select*`/`Lista*` respondem
 * `resultado: false`, `data: null` e a mensagem "Nenhum registro encontrado"
 * em vez de `Success([], 0)` quando não há linha. Passar isso pelo `unwrap`
 * comum viraria BusinessError e a tela mostraria falha onde só havia ausência
 * de dado.
 *
 * O reconhecimento é ESTREITO de propósito: `resultado: false` **e** payload
 * ausente **e** a mensagem canônica. Um `resultado: false` com mensagem de
 * verdade continua sendo erro.
 */
const MENSAGEM_SEM_REGISTRO = /nenhum\s+registro\s+encontrado/i;

export function respostaSemRegistro(resposta: ApiResponse<unknown>): boolean {
  return (
    resposta?.status?.resultado === false &&
    resposta.data === null &&
    MENSAGEM_SEM_REGISTRO.test(resposta.status.mensagem ?? '')
  );
}

/**
 * `unwrap` para endpoints cujo payload é uma LISTA — mesma regra, exceto que o
 * dialeto de lista vazia acima vira `[]` em vez de erro.
 */
export function unwrapLista<T>(resposta: ApiResponse<T[] | null>): Unwrapped<T[] | null> {
  if (respostaSemRegistro(resposta)) return { data: [], totalItens: 0 };
  return unwrap(resposta);
}
